import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faChevronRight,
    faChevronLeft,
    faPen,
    faThLarge,
    faTimes,
    faWallet,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button as SharedButton } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import {
    computeEqualSplitAmount,
} from './splitPayment.utils';
import { useSplitPaymentDialogState } from './SplitPaymentDialog.logic';
import orderClasses from '../section/order-detail.module.scss';

type SplitPaymentDialogProps = {
    isOpen: boolean;
    totalAmount: number;
    /** with-tip grand total shown + split across all tabs (summary + equal + custom); defaults to totalAmount */
    equalSplitTotal?: number;
    canOpenByProduct?: boolean;
    isSubmitting?: boolean;
    onClose: () => void;
    onOpenByProduct?: () => void;
    onConfirmEqual: (count: number, amounts: number[]) => void;
    onConfirmCustom: (customAmount: number, remainderAmount: number) => void;
};

export const SplitPaymentDialog: React.FC<SplitPaymentDialogProps> = ({
    isOpen,
    totalAmount,
    equalSplitTotal = totalAmount,
    canOpenByProduct = true,
    isSubmitting = false,
    onClose,
    onOpenByProduct,
    onConfirmEqual,
    onConfirmCustom,
}) => {
    const {
        mode,
        equalSplitCount,
        customSplitInput,
        equalSplitAmount,
        equalSplitAmounts,
        customSplitAmount,
        customSplitRemainder,
        customValid,
        setMode,
        setEqualSplitCount,
        handleCustomSplitInputChange,
    } = useSplitPaymentDialogState({ isOpen, totalAmount, equalSplitTotal });

    if (!isOpen) {
        return null;
    }

    return (
        <div className={orderClasses.splitDialogBackdrop} role="presentation" onClick={onClose}>
            <section
                className={orderClasses.splitDialog}
                role="dialog"
                aria-label="חלוקת תשלום"
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className={orderClasses.splitDialogClose}
                    onClick={onClose}
                    aria-label="סגור חלוקה"
                    data-testid="split-payment-close"
                >
                    <FontAwesomeIcon icon={faTimes as IconProp} />
                </button>

                {mode === 'root' && (
                    <>
                        <header className={orderClasses.splitDialogHeader}>
                            <h2 className={orderClasses.splitDialogTitle}>חלוקת תשלום</h2>
                            <div className={orderClasses.splitSummaryCard}>
                                <span className={orderClasses.splitSummaryLabel}>סכום נוכחי</span>
                                <span className={orderClasses.splitSummaryValue}>
                                    <span className={orderClasses.splitSummaryCurrency}>₪</span>
                                    {formatMoney(equalSplitTotal)}
                                </span>
                            </div>
                        </header>

                        <div className={orderClasses.splitOptionsList}>
                            <button
                                type="button"
                                className={`${orderClasses.splitOptionRow} ${orderClasses.splitOptionRowCyan}`}
                                onClick={() => onOpenByProduct?.()}
                                disabled={isSubmitting}
                                data-testid="split-payment-by-product"
                            >
                                <span className={orderClasses.splitOptionArrow}>
                                    <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                                </span>
                                <span className={orderClasses.splitOptionBody}>
                                    <span className={orderClasses.splitOptionTitle}>תשלום לפי פריטים</span>
                                    <span className={orderClasses.splitOptionSub}>פצל שורה על סך פריטים כולל</span>
                                </span>
                                <span className={`${orderClasses.splitOptionIcon} ${orderClasses.splitOptionIconCyan}`}>
                                    <FontAwesomeIcon icon={faWallet as IconProp} />
                                </span>
                            </button>

                            <button
                                type="button"
                                className={`${orderClasses.splitOptionRow} ${orderClasses.splitOptionRowGreen}`}
                                onClick={() => setMode('equal')}
                                disabled={isSubmitting}
                                data-testid="split-payment-mode-equal"
                            >
                                <span className={orderClasses.splitOptionArrow}>
                                    <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                                </span>
                                <span className={orderClasses.splitOptionBody}>
                                    <span className={orderClasses.splitOptionTitle}>חלוקה לשורות שוות</span>
                                    <span className={orderClasses.splitOptionSub}>פצל לשורות שוות בהתאם למספר שתבחר</span>
                                </span>
                                <span className={`${orderClasses.splitOptionIcon} ${orderClasses.splitOptionIconGreen}`}>
                                    <FontAwesomeIcon icon={faThLarge as IconProp} />
                                </span>
                            </button>

                            <button
                                type="button"
                                className={`${orderClasses.splitOptionRow} ${orderClasses.splitOptionRowPurple}`}
                                onClick={() => setMode('custom')}
                                disabled={isSubmitting}
                                data-testid="split-payment-mode-custom"
                            >
                                <span className={orderClasses.splitOptionArrow}>
                                    <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                                </span>
                                <span className={orderClasses.splitOptionBody}>
                                    <span className={orderClasses.splitOptionTitle}>חלוקה בהתאמה אישית</span>
                                    <span className={orderClasses.splitOptionSub}>הכנס סכום מדויק והשורה תחולק לסכום שנבחר + יתרה</span>
                                </span>
                                <span className={`${orderClasses.splitOptionIcon} ${orderClasses.splitOptionIconPurple}`}>
                                    <FontAwesomeIcon icon={faPen as IconProp} />
                                </span>
                            </button>
                        </div>
                    </>
                )}

                {mode === 'equal' && (
                    <div className={orderClasses.splitView}>
                        <button type="button" className={orderClasses.splitBackButton} onClick={() => setMode('root')} data-testid="split-payment-equal-back">
                            <FontAwesomeIcon icon={faChevronRight as IconProp} />

                        </button>
                        <h2 className={orderClasses.splitDialogTitleEqual}>חלוקה שווה</h2>
                        <div className={orderClasses.splitDualSummaryDarkEqual}>
                            <div className={orderClasses.splitDualCol}>
                                <span className={orderClasses.splitDualValueDarkStrong}>
                                    <span className={orderClasses.splitDualCurrency}>₪</span>
                                    {formatMoney(equalSplitTotal)}
                                </span>
                                <span className={orderClasses.splitDualLabelDark}>סכום כולל לפני חלוקה</span>
                            </div>
                            <div className={orderClasses.splitDualDividerDark} />
                            <div className={orderClasses.splitDualCol}>
                                <span className={orderClasses.splitDualValueDark}>
                                    {equalSplitCount ? `${formatMoney(equalSplitAmount)} ₪` : '—'}
                                </span>
                                <span className={orderClasses.splitDualLabelDark}>לכל משלם</span>
                                {!equalSplitCount && (
                                    <span className={orderClasses.splitDualHint}>בחר מספר</span>
                                )}
                            </div>
                        </div>

                        <div className={orderClasses.splitGridLabel}>בחר מספר משלמים</div>
                        <div className={orderClasses.splitCountGrid}>
                            {[2, 3, 4, 5, 6, 7, 8, 9].map((count) => {
                                const tileAmount = computeEqualSplitAmount(equalSplitTotal, count);
                                const selected = equalSplitCount === count;
                                return (
                                    <button
                                        key={count}
                                        type="button"
                                        className={`${orderClasses.splitCountTile} ${selected ? orderClasses.splitCountTileActive : ''}`}
                                        onClick={() => setEqualSplitCount(count)}
                                        data-testid={`split-payment-count-${count}`}
                                    >
                                        <span className={orderClasses.splitCountNumber}>{count}</span>
                                        <span className={orderClasses.splitCountAmount}>{formatMoney(tileAmount)} ₪</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className={orderClasses.splitEqualPreview}>
                            <div className={orderClasses.splitPreviewLabel}>תצוגה מקדימה</div>
                            {equalSplitCount ? (
                                <div className={orderClasses.splitPreviewChips}>
                                    {equalSplitAmounts.map((lineAmount, idx) => (
                                        <span key={idx + 1} className={orderClasses.splitPreviewChip}>{idx + 1} • {formatMoney(lineAmount)} ₪</span>
                                    ))}
                                </div>
                            ) : (
                                <div className={orderClasses.splitPreviewEmpty}>בחר מספר משלמים כדי לראות את החלוקה</div>
                            )}
                        </div>

                        <SharedButton
                            variant="confirm"
                            size="lg"
                            fullWidth
                            onClick={() => { if (equalSplitCount) onConfirmEqual(equalSplitCount, equalSplitAmounts); }}
                            disabled={!equalSplitCount || isSubmitting}
                            dataTestId="split-payment-equal-confirm"
                        >
                            <FontAwesomeIcon icon={faCheck as IconProp} />
                            אשר חלוקה
                        </SharedButton>
                    </div>
                )}


                {mode === 'custom' && (
                    <div className={orderClasses.splitView}>
                        <button type="button" className={orderClasses.splitBackButton} onClick={() => setMode('root')} data-testid="split-payment-custom-back">
                            <FontAwesomeIcon icon={faChevronRight as IconProp} />
                            חזרה
                        </button>
                        <h2 className={orderClasses.splitDialogTitle}>חלוקה בהתאמה אישית</h2>
                        <div className={orderClasses.splitDualSummaryDark}>
                            <div className={orderClasses.splitDualCol}>
                                <span className={orderClasses.splitDualValueDarkStrong}>
                                    <span className={orderClasses.splitDualCurrency}>₪</span>
                                    {formatMoney(equalSplitTotal)}
                                </span>
                                <span className={orderClasses.splitDualLabelDark}>סכום כולל לפני חלוקה</span>
                            </div>
                            <div className={orderClasses.splitDualDividerDark} />
                            <div className={orderClasses.splitDualCol}>
                                <span className={orderClasses.splitDualValueDark}>
                                    {customSplitAmount > 0 ? `${formatMoney(customSplitRemainder)} ₪` : '—'}
                                </span>
                                <span className={orderClasses.splitDualLabelDark}>יתרה אחרי פיצול</span>
                            </div>
                        </div>

                        <div className={orderClasses.splitGridLabel}>הכנס סכום לפיצול (חייב להיות קטן מהסכום הכולל)</div>
                        <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="0.00"
                            value={customSplitInput}
                            onChange={(event) => handleCustomSplitInputChange(event.currentTarget.value)}
                            className={orderClasses.splitCustomInput}
                            aria-label="הכנס סכום לפיצול"
                            data-testid="split-payment-custom-input"
                        />

                        <SharedButton
                            variant="confirm"
                            size="lg"
                            fullWidth
                            onClick={() => onConfirmCustom(customSplitAmount, customSplitRemainder)}
                            disabled={!customValid || isSubmitting}
                            dataTestId="split-payment-custom-confirm"
                        >
                            <FontAwesomeIcon icon={faCheck as IconProp} />
                            אשר חלוקה
                        </SharedButton>
                    </div>
                )}
            </section>
        </div>
    );
};
